import { useIsFeatureEnabled } from "@fero/hooks";
import { useTradeSalesStore } from "@fero/stores";
import { AppRoutes } from "@fero/utils";
import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Button, Grid, Stack } from "@mui/material";
import type { BigDecimal } from "adl-gen/common";
import type {
  ProductId,
  PurchaseRequestSalePriceType,
} from "adl-gen/ferovinum/app/db";
import { useConfirmationDialog } from "components/context/global-dialog/use-dialog";
import { PurchaseOrderUploadCard } from "components/library/widgets/wizard-card/purchase-order-upload-card";
import type { FormikProps } from "formik";
import { useMemo } from "react";
import type { SelectedProductsFormValues } from "../new-organisation-purchase-request-setup-page";
import {
  DeliveryTypeCard,
  NewLocationSelectorCard,
  SalePriceTypeSelectorCard,
} from ".";
import { AttributeSelectionCard } from "./attribute-selection-card";
import { NewProductSelectionCard } from "./new-product-selection-card";

interface SetupFormSectionProps {
  savedPurchaserDiscountPercentage?: BigDecimal;
  poUploadEnabled: boolean;
  form: FormikProps<SelectedProductsFormValues>;
  resetFormProductsToInitial: () => void;
  producibles: Map<ProductId, string>;
  isProducibleDataLoading: boolean;
}

export const SetupFormSection = (props: SetupFormSectionProps) => {
  const {
    form,
    resetFormProductsToInitial,
    poUploadEnabled,
    savedPurchaserDiscountPercentage,
    producibles,
    isProducibleDataLoading,
  } = props;
  const showAttributeSelectionCard = useIsFeatureEnabled(
    "trade_sale_product_attribute_selection",
  );
  const isAttributeSelectionRequired =
    showAttributeSelectionCard &&
    form.values.selectedProducts.length > 0 &&
    form.values.selectedProducts.some(
      (product) => product.summary.value.batchRequired,
    );
  const { options, updateOptions } = useTradeSalesStore();
  const { showConfirmationDialog } = useConfirmationDialog();

  const onChangeSalePriceType = async (value: PurchaseRequestSalePriceType) => {
    let shouldUpdate = true;
    if (form.values.selectedProducts?.length) {
      const userResponse = await showConfirmationDialog({
        title: "Are you sure you want to change the sale price type?",
        body: "You will lose your current product selections and price/quantity details.",
      });
      shouldUpdate = userResponse.kind === "ok";
    }

    if (shouldUpdate) {
      form.setFieldValue("salePriceType", value);
      updateOptions({ salePriceType: value });
      resetFormProductsToInitial();
    }
  };

  const onChangeDeliveryType = async (isBonded: boolean) => {
    form.setFieldValue("bondedSale", isBonded);
    if (isGbLocation) {
      const salePriceType = isBonded ? "exDutyAndVat" : "incDutyExVat";
      form.setFieldValue("salePriceType", salePriceType);
      updateOptions({ salePriceType });
    } else {
      const salePriceType = isBonded ? "exDutyAndVat" : "incDutyAndVat";
      form.setFieldValue("salePriceType", salePriceType);
      updateOptions({ salePriceType });
    }
  };

  const isNextButtonDisabled = useMemo(() => {
    const hasSelectedProducts = form.values.selectedProducts.length > 0;
    const hasSetProductAmounts =
      hasSelectedProducts &&
      !form.values.selectedProducts.some((p) => {
        if (p.freeUnits) return false; // in this case we will be using free stock instead of quantity
        return p.quantity <= 0;
      });
    const hasNoProductErrors = Object.values(
      form.errors.selectedProducts || {},
    ).every((product) => Object.keys(product).length === 0);
    const hasFreeStock = form.values.selectedProducts.some(
      (product) => product.freeUnits > 0,
    );
    // If free stock exists, ensure at least one product with quantity > 0
    const hasValidProductForFreeStock =
      hasFreeStock &&
      !form.values.selectedProducts.some((product) => product.quantity > 0);

    const hasProductPricing = form.values.selectedProducts.every(
      (product) =>
        (product.freeUnits > 0 &&
          product.quantity > 0 &&
          product.salePrice > 0) ||
        (product.freeUnits > 0 && product.quantity === 0) ||
        product.salePrice > 0,
    );

    const hasAttributeSelection = isAttributeSelectionRequired
      ? form.values.selectedProducts.every((product) =>
          product.summary.value.batchRequired
            ? Object.values(product.attributeSelection).reduce(
                (acc, attribute) => acc + Number(attribute.quantity),
                0,
              ) ===
              product.quantity + product.freeUnits
            : true,
        )
      : true;

    return (
      !hasSelectedProducts ||
      !hasSetProductAmounts ||
      !hasNoProductErrors ||
      hasValidProductForFreeStock ||
      !hasProductPricing ||
      !hasAttributeSelection
    );
  }, [form.values.selectedProducts, form.errors, isAttributeSelectionRequired]);

  const isGbLocation = options.storageLocation?.country === "GB";

  return (
    <>
      {poUploadEnabled && (
        <Grid item xs={12}>
          <PurchaseOrderUploadCard
            file={form.values.poFile}
            onFileSelected={(file) => form.setFieldValue("poFile", file)}
            onFileRemoved={() => form.setFieldValue("poFile", undefined)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <NewLocationSelectorCard form={form} />
      </Grid>
      <Grid item xs={12}>
        <DeliveryTypeCard
          isBonded={form.values.bondedSale}
          onChange={onChangeDeliveryType}
        />
        {!form.values.bondedSale && isGbLocation && (
          <Grid item xs={12}>
            <SalePriceTypeSelectorCard
              salePrice={options.salePriceType ?? form.values.salePriceType}
              onChange={onChangeSalePriceType}
            />
          </Grid>
        )}
      </Grid>
      {options.storageLocation && (
        <Grid item xs={12}>
          {!form.values.bondedSale && !isGbLocation && (
            <Alert severity="info">
              Please ensure the sale price entered includes all fees such as
              Duty and VAT
            </Alert>
          )}
          <NewProductSelectionCard
            form={form}
            savedPurchaserDiscountPercentage={savedPurchaserDiscountPercentage}
            producibles={producibles}
            isProducibleDataLoading={isProducibleDataLoading}
          />
        </Grid>
      )}
      {isAttributeSelectionRequired && (
        <Grid item xs={12} sx={{ marginTop: 2 }}>
          <AttributeSelectionCard form={form} />
        </Grid>
      )}
      <Grid item xs={12}>
        <Stack spacing={2} direction="row" justifyContent={"flex-end"}>
          <Button variant="outlined" href={AppRoutes.Dashboard}>
            Back
          </Button>
          <LoadingButton
            variant="contained"
            onClick={form.submitForm}
            disabled={isNextButtonDisabled}
          >
            Next
          </LoadingButton>
        </Stack>
      </Grid>
    </>
  );
};
